import { useMutation, useQueryClient } from "@tanstack/react-query"
import { API, Auth } from "aws-amplify"
import { useQuery } from "@tanstack/react-query"

const fetchCreateTax = async ({ organizationId, taxData }) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const defaultTax = {
    name: "",
    type: "tax",
    value: 0,
    active: true,
  }
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
    body: taxData ?? defaultTax,
  }
  const response = await API.post(
    "CORE",
    `/taxes/?organization_id=${organizationId}`,
    payload,
  )
  return response
}

export const useCreateTax = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fetchCreateTax,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["taxes", variables.organizationId])
    },
  })
}

const fetchTaxes = async organizationId => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }
  const response = await API.get(
    "CORE",
    `/taxes?organization_id=${organizationId}`,
    payload,
  )

  if (Array.isArray(response)) {
    const typeOrder = {
      tax: 0,
      retention: 1,
      surcharge: 2,
    }
    response.sort((a, b) => {
      const orderA = typeOrder[a.type]
      const orderB = typeOrder[b.type]

      if (orderA !== orderB) {
        return orderA - orderB
      }

      const nameA = a.longName?.toLowerCase() || ""
      const nameB = b.longName?.toLowerCase() || ""

      const firstWordA = nameA.split(" ")[0]
      const firstWordB = nameB.split(" ")[0]
      if (firstWordA !== firstWordB) {
        return firstWordB.localeCompare(firstWordA)
      }

      return b.value - a.value
    })
  }
  return response
}

export const useTaxes = (organizationId, enabled = false) => {
  return useQuery({
    queryKey: ["taxes", organizationId],
    queryFn: () => fetchTaxes(organizationId),
    enabled,
  })
}

const fetchEditTax = async tax => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
  }

  const { PK: organization_id, SK: tax_id, active: status } = tax
  const url = `/taxes/?organization_id=${organization_id}&tax_id=${tax_id}&status=${status}`
  const response = await API.put("CORE", url, payload)
  return response
}

export const useEditTax = organizationId => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: tax => fetchEditTax({ ...tax, PK: organizationId }),
    onSuccess: () => {
      queryClient.invalidateQueries(["taxes", organizationId])
    },
  })
}

const fetchDeleteTax = async ({ organizationId, sk }) => {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  const payload = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Access-Control-Allow-Origin": "*",
    },
    body: {
      organization_id: organizationId,
      sks: [sk],
    },
  }
  const response = await API.del("CORE", "/taxes/", payload)
  return response
}

export const useDeleteTax = organizationId => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ sk }) => fetchDeleteTax({ organizationId, sk }),
    onSuccess: () => {
      queryClient.invalidateQueries(["taxes", organizationId])
    },
  })
}
